/**
 * @file shipping-method-utils.ts
 *
 * Pure helpers for shipping methods and reordering, kept apart from the
 * components so they stay independently testable: the distinct method-type ids on
 * a zone (for the overview summary), the flat-rate cost-formula parsing (base
 * cost and the optional per-item quantity fee WooCommerce encodes as
 * `"cost + (fee * [qty])"`), the free-shipping "needs a minimum amount" predicate,
 * and the array reorder used by the up/down buttons.
 */

import type { ShippingMethod } from '../api/shop-types';
import {
  FLAT_RATE,
  FREE_SHIPPING,
  SHIPPING_REQUIREMENT_OPTIONS,
} from './shipping-constants';

/**
 * Returns the distinct known method-type ids present on a zone, in first-seen
 * order (used to summarise a zone's methods in the overview).
 *
 * @param methods - The zone's methods.
 *
 * @returns The distinct `"flat_rate"` / `"free_shipping"` ids present.
 */
export function methodTypeIds(methods: ShippingMethod[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const method of methods) {
    const id = method.method_id;
    if ((id === FLAT_RATE || id === FREE_SHIPPING) && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

/**
 * Extracts the base cost from a flat-rate cost formula, dropping the optional
 * per-item fee term (`"5 + (2 * [qty])"` → `"5"`).
 *
 * @param cost - The stored cost formula (may be empty).
 *
 * @returns The base cost string (empty when unset).
 */
export function parseFlatRateBaseCost(cost: string): string {
  const feeIndex = cost.indexOf(' + (');
  return (feeIndex >= 0 ? cost.slice(0, feeIndex) : cost).trim();
}

/**
 * Whether a flat-rate cost formula includes a per-item quantity fee.
 *
 * @param cost - The stored cost formula.
 *
 * @returns `true` when a `[qty]` fee term is present.
 */
export function hasQuantityFee(cost: string): boolean {
  return cost.includes('[qty]');
}

/**
 * Extracts the per-item fee amount from a flat-rate cost formula.
 *
 * @param cost - The stored cost formula.
 *
 * @returns The fee amount string, or `undefined` when there is no fee term.
 */
export function parseFlatRateQuantityFee(cost: string): string | undefined {
  const match = cost.match(/\+\s*\(\s*([0-9.]+)\s*\*\s*\[qty\]\s*\)/);
  return match ? match[1] : undefined;
}

/**
 * Whether a free-shipping `requires` option needs a minimum order amount.
 *
 * @param value - The `requires` value (e.g. `"min_amount"`, `"coupon"`).
 *
 * @returns `true` when the option requires a minimum amount.
 */
export function shippingRequirementNeedsMinimumAmount(value: string): boolean {
  return (
    SHIPPING_REQUIREMENT_OPTIONS.find((option) => option.value === value)
      ?.requiresMinimumAmount ?? false
  );
}

/**
 * Returns a copy of `arr` with the item at `from` moved to `to`. Out-of-range
 * indices return the original array unchanged (so the up/down buttons at the ends
 * are no-ops).
 *
 * @param arr - The source array.
 * @param from - The current index.
 * @param to - The target index.
 *
 * @returns A new reordered array, or the original when the move is out of range.
 */
export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) {
    return arr;
  }
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
